import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import type { ChatMessage as ChatMessageType } from '../../lib/twitch.js';
import type { ChannelEvent } from '../../lib/events.js';
import { useAuth } from '../../context/AuthContext.js';
import ChatMessage from './ChatMessage.js';
import ChatSystemLine, { type ChatVisibleEvent } from './ChatSystemLine.js';
import { useEmotes } from '../../hooks/useEmotes.js';
import { useBadges } from '../../hooks/useBadges.js';
import { useChannelAvatars } from '../../hooks/useChannelAvatars.js';
import { apiFetch } from '../../lib/api.js';

interface Props {
  messages: ChatMessageType[];
  deletedIds: Set<string>;
  quotaWarning?: boolean;
  onOpenUserInfo?: (twitchUserId: string) => void;
  redemptionMap?: Map<string, string>;
  onReply?: (msg: ChatMessageType) => void;
  /**
   * Channel events that carry a share-worthy chat message (announcements, and
   * resub/sub/viewer-milestone shares that included one) — shown inline in the
   * message stream itself, in addition to the mod-only Aktivitäten feed.
   */
  channelEvents?: ChannelEvent[];
  /**
   * The highest `receivedAt` among the initially-loaded channelEvents history
   * (see useChatStream.ts). Only events newer than this are shown inline in
   * chat — historical ones still reach the Aktivitäten feed unfiltered.
   */
  chatEventsCutoff?: number;
}

const SCROLL_THRESHOLD_PX = 80;

/** Type guard: is this a channel event worth showing inline in the public chat message stream? */
function isChatVisibleEvent(ev: ChannelEvent): ev is ChatVisibleEvent {
  if (ev.type === 'announcement') return true;
  if (ev.type === 'sub_share_message') return true;
  if (ev.type === 'resub') return Boolean(ev.message);
  if (ev.type === 'viewer_milestone') return Boolean(ev.message);
  return false;
}

type TimelineItem =
  | { kind: 'message'; receivedAt: number; message: ChatMessageType }
  | { kind: 'event'; receivedAt: number; event: ChatVisibleEvent };

export default function ChatList({ messages, deletedIds, quotaWarning = false, onOpenUserInfo, redemptionMap, onReply, channelEvents, chatEventsCutoff = 0 }: Props) {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const { isMod } = useAuth();
  const [broadcasterTwitchId, setBroadcasterTwitchId] = useState<string | null>(null);
  const emotes = useEmotes(broadcasterTwitchId);
  const { badgeMap, broadcasterAvatar } = useBadges();
  const channelAvatars = useChannelAvatars(messages);

  // The Aktivitäten feed (EventFeed.tsx, mod-only) intentionally shows the full
  // channel-events history regardless of page reloads — it's a running log.
  // Inline-in-chat is different: showing e.g. an announcement from hours/days
  // ago at the top of the chat on every reload would look like it just
  // happened. So only events newer than `chatEventsCutoff` (the highest
  // `receivedAt` among the initially-loaded history, computed in
  // useChatStream.ts) are shown inline — historical ones are excluded here,
  // but still reach EventFeed via the same `channelEvents` prop unfiltered.
  // Deliberately NOT compared against the client's own Date.now(): `receivedAt`
  // is always server-clock-derived, and comparing it to a client-side
  // timestamp would misbehave under client/server clock drift.
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = messages.map((m) => ({
      kind: 'message',
      receivedAt: m.receivedAt,
      message: m,
    }));
    for (const ev of channelEvents ?? []) {
      if (ev.receivedAt > chatEventsCutoff && isChatVisibleEvent(ev)) {
        items.push({ kind: 'event', receivedAt: ev.receivedAt, event: ev });
      }
    }
    items.sort((a, b) => a.receivedAt - b.receivedAt);
    return items;
  }, [messages, channelEvents, chatEventsCutoff]);

  // Only show per-channel avatars when messages come from multiple different channels
  // (i.e. during a Squad Stream / Shared Chat session). Single-channel streams
  // just show the regular Twitch icon — no need to stamp every message with the
  // same broadcaster avatar.
  const isMultiChannel = useMemo(() => {
    const channels = new Set<string>();
    for (const m of messages) {
      if (m.source === 'twitch' && m.channel) channels.add(m.channel.toLowerCase());
      if (channels.size > 1) return true;
    }
    return false;
  }, [messages]);

  useEffect(() => {
    apiFetch<{ broadcasterTwitchId: string | null }>('/api/config')
      .then((cfg) => { if (cfg.broadcasterTwitchId) setBroadcasterTwitchId(cfg.broadcasterTwitchId); })
      .catch(() => {});
  }, []);

  // Auto-scroll only while the user is near the bottom.
  useEffect(() => {
    if (!pinnedToBottom) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [timeline, pinnedToBottom]);

  function handleScroll(e: UIEvent<HTMLDivElement>): void {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    setPinnedToBottom(distanceFromBottom < SCROLL_THRESHOLD_PX);
  }

  function jumpToBottom(): void {
    setPinnedToBottom(true);
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }

  return (
    <div className="relative flex-1 min-h-0">
      <div
        onScroll={handleScroll}
        className="absolute inset-0 overflow-y-auto py-1"
      >
        {quotaWarning && isMod && (
          <div className="bg-yellow-500/20 border border-yellow-500 text-yellow-200 text-sm px-3 py-2 rounded mb-2 mx-3">
            ⚠ YouTube-Chat pausiert (Tages-Quota erreicht)
          </div>
        )}
        {timeline.length === 0 ? (
          <div className="text-center text-slate-500 text-sm mt-10 px-4">
            Warte auf Nachrichten…
          </div>
        ) : (
          timeline.map((item, idx) => {
            if (item.kind === 'event') {
              const ev = item.event;
              // idx disambiguates events with an empty username arriving in the same
              // millisecond (receivedAt has only 1ms resolution) — without it, two such
              // events would share an identical key and React could mix up their DOM nodes.
              return <ChatSystemLine key={`event-${idx}-${ev.type}-${ev.receivedAt}`} event={ev} />;
            }
            const m = item.message;
            return (
              <ChatMessage
                key={m.externalId}
                msg={m}
                deleted={deletedIds.has(m.externalId)}
                onOpenUserInfo={onOpenUserInfo}
                emotes={emotes}
                badgeMap={badgeMap}
                broadcasterAvatar={broadcasterAvatar}
                redemptionMap={redemptionMap}
                channelAvatarUrl={
                  m.source === 'twitch' && isMultiChannel
                    ? channelAvatars.get(m.channel?.toLowerCase() ?? '') ?? undefined
                    : undefined
                }
                onReply={onReply}
              />
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      {!pinnedToBottom && (
        <button
          type="button"
          onClick={jumpToBottom}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-purple-700 hover:bg-purple-600 text-white text-xs px-3 py-1.5 rounded-full shadow-lg"
        >
          ↓ Zum aktuellen Chat
        </button>
      )}
    </div>
  );
}
